import React, { useCallback, useMemo, useRef } from "react";
// @ts-ignore - react-big-calendar types
import { momentLocalizer } from "react-big-calendar";
import { Box, Text } from "@mantine/core";
import moment from "moment-timezone";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useAuth } from "../../../../../context/AuthContext";
import { useCalendar } from "../../../../../context/CalendarContext";
import { useSalon } from "../../../../../context/SalonContext";
import {
  CalendarEventType,
  SlotInfo as CalendarSlotInfo,
} from "../../../../../types/calendar";
import { BusinessInfo } from "../../../../../types/context";
import { notifyWarning } from "../../../../../utils/notify";
import CalendarToolbar from "./CalendarToolbar";
import DnDCalendar, {
  CalendarDropInfo,
  CalendarResizeInfo,
} from "./DnDCalendar";
import {
  buildOverridesMap,
  buildResources,
  createHandleSelectSlot,
  createSlotPropGetter,
  getInitials,
  createSalonSlotPropGetter,
  isEventDraggable,
  eventTooltipAccessor,
  getSalonNow,
} from "./sharedCalendarUtils";
import {
  GRID_STEP_MINS,
  GRID_TIMESLOTS,
  NowGutterWrapper,
  openingScrollTime,
  SlotHoverWrapper,
  StaffColumnHeader,
} from "./gridParts";
import { laneLayout, laneOverflow } from "./laneLayout";
import { useGridZoom } from "./useGridZoom";
import {
  EventBlock,
  EventBlockContext,
  makeEventPropGetter,
  type EventFlag,
} from "./EventBlock";
import styles from "./Views.module.css";

const localizer = momentLocalizer(moment);

/** Lane caps (prototype): Combined shows 4 lanes, Split 2, then "+N". */
const COMBINED_LAYOUT = laneLayout({ gap: 3, cap: 4 });
const SPLIT_LAYOUT = laneLayout({ gap: 3, cap: 2 });

// ── RBC custom components ────────────────────────────────────────────────
// All module scope, and the `components` object is a module constant: RBC
// remounts a custom component whenever the `components` identity changes,
// which would drop the toolbar's local state (open Filters / date picker
// popover) on every filter toggle or navigation, and can swallow a click that
// lands mid-remount. Changing data is read from context inside each component
// (CalendarContext / SalonContext, or EventBlockContext), never closed over.

interface RbcToolbarProps {
  date: Date;
  onNavigate: (action: "PREV" | "NEXT" | "TODAY") => void;
}

const WeekToolbar = ({ date, onNavigate }: RbcToolbarProps) => (
  <CalendarToolbar date={date} view="week" onNavigate={onNavigate} />
);

// Day column header: weekday stacked over the date; today gets a teal
// circle and a tinted header.
const CustomHeader = ({ date }: { date: Date }) => {
  const { businessInfo } = useSalon() as { businessInfo: BusinessInfo | null };
  const timezone = businessInfo?.timezone;
  const isToday = moment(date).isSame(getSalonNow(timezone), "day");

  return (
    <Box className={styles.weekViewHeader} data-today={isToday || undefined}>
      <Text component="span" className={styles.weekViewDayName}>
        {moment(date).format("ddd")}
      </Text>
      <Text component="span" className={styles.weekViewDayNumber}>
        {moment(date).format("D")}
      </Text>
    </Box>
  );
};

const WEEK_COMPONENTS = {
  toolbar: WeekToolbar,
  resourceHeader: StaffColumnHeader,
  event: EventBlock,
  header: CustomHeader,
  timeSlotWrapper: SlotHoverWrapper,
  timeGutterWrapper: NowGutterWrapper,
} as any;

interface WeekViewProps {
  events: CalendarEventType[];
  onSelectEvent: (event: CalendarEventType) => void;
  /** Open an event's detail modal directly on double-click. */
  onDoubleClickEvent?: (event: CalendarEventType) => void;
  onSelectSlot?: (slotInfo: CalendarSlotInfo) => void;
  onEventDrop?: (info: CalendarDropInfo) => void;
  onEventResize?: (info: CalendarResizeInfo) => void;
  /** Override which events can be dragged (defaults to isEventDraggable). */
  draggableAccessor?: (event: CalendarEventType) => boolean;
  /** Override which events can be resized (defaults to isEventDraggable). */
  resizableAccessor?: (event: CalendarEventType) => boolean;
  /** Master toggle for resize handles (defaults to true). */
  resizable?: boolean;
  /** Flag chips for a block (calendarFlags over the store). */
  flagsForEvent?: (event: CalendarEventType) => EventFlag[];
}

const WeekView: React.FC<WeekViewProps> = ({
  events,
  onSelectEvent,
  onDoubleClickEvent,
  onSelectSlot,
  onEventDrop,
  onEventResize,
  draggableAccessor = isEventDraggable,
  resizableAccessor = isEventDraggable,
  resizable = true,
  flagsForEvent,
}) => {
  const {
    filteredEmployees,
    selectedDate,
    setSelectedDate,
    weekDisplay,
    hoursOverrides,
    zoom,
    setZoom,
  } = useCalendar();
  const { currentUser } = useAuth() as {
    currentUser: { userId: string } | null;
  };
  const { businessInfo } = useSalon() as { businessInfo: BusinessInfo | null };
  const timezone = businessInfo?.timezone;
  const split = weekDisplay === "split";

  // Opens at the salon's opening time minus 30 min; zoom keeps the top
  // minute anchored (useGridZoom owns the scroll, not RBC).
  const opening = openingScrollTime(businessInfo, selectedDate);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const zoomStyle = useGridZoom(containerRef, {
    zoom,
    setZoom,
    openingMinutes: opening.getHours() * 60 + opening.getMinutes(),
  });

  // Combined: each block carries its staff member's photo (else initials).
  const blockContext = useMemo(() => {
    const names = new Map(
      filteredEmployees.map((e) => [e.id, `${e.first_name} ${e.last_name}`]),
    );
    const photos = new Map(
      filteredEmployees.map((e) => [
        e.id,
        e.staff_photo_url ?? e.avatar_url ?? null,
      ]),
    );
    const staffIdOf = (event: CalendarEventType) =>
      ("employeeId" in event && event.employeeId) || event.resourceId;
    return {
      mode: "week" as const,
      timezone,
      flagsFor: flagsForEvent,
      initialsFor: split
        ? undefined
        : (event: CalendarEventType) => {
            const id = staffIdOf(event);
            const name =
              ("employeeName" in event && event.employeeName) ||
              (id ? names.get(id) : "");
            return name ? getInitials(name) : null;
          },
      photoFor: split
        ? undefined
        : (event: CalendarEventType) => {
            const id = staffIdOf(event);
            const src = id ? photos.get(id) : null;
            return src ? { src, name: names.get(id as string) ?? "" } : null;
          },
    };
  }, [filteredEmployees, split, timezone, flagsForEvent]);

  // Split: each staff member's week side by side (RBC renders resources
  // first: every resource's 7 days in a row), shaded by their own hours.
  const userId = currentUser?.userId;
  const resources = useMemo(
    () => (split ? buildResources(filteredEmployees, userId) : undefined),
    [split, filteredEmployees, userId],
  );
  const overridesMap = useMemo(
    () => buildOverridesMap(hoursOverrides),
    [hoursOverrides],
  );

  // Block look (incl. the lane-cap "+N" chip / hidden runs). The chip is
  // display-only: no click, drag or resize (below).
  const eventPropGetter = useMemo(
    () => makeEventPropGetter(timezone, "week"),
    [timezone],
  );
  const tooltipAccessor = (event: CalendarEventType) => {
    const overflow = laneOverflow(event);
    return overflow?.kind === "more"
      ? overflow.title
      : eventTooltipAccessor(event);
  };
  const handleSelectEvent = useCallback(
    (event: CalendarEventType, e?: React.SyntheticEvent) => {
      if (laneOverflow(event)) return;
      (
        onSelectEvent as (
          ev: CalendarEventType,
          e?: React.SyntheticEvent,
        ) => void
      )(event, e);
    },
    [onSelectEvent],
  );
  const handleDoubleClickEvent = useCallback(
    (event: CalendarEventType) => {
      if (laneOverflow(event)) return;
      onDoubleClickEvent?.(event);
    },
    [onDoubleClickEvent],
  );
  const dragAccessor = (event: CalendarEventType) =>
    !laneOverflow(event) && draggableAccessor(event);
  const resizeAccessor = (event: CalendarEventType) =>
    !laneOverflow(event) && resizableAccessor(event);

  // Split: a drag stays on its own staff member's week (the design's "Same
  // provider only in week view"); a drop on another staff's days is refused.
  const handleEventDrop = useCallback(
    (info: CalendarDropInfo) => {
      const from = info.event.resourceId;
      if (split && info.resourceId && from && info.resourceId !== from) {
        notifyWarning("Same provider only in week view");
        return;
      }
      onEventDrop?.(info);
    },
    [split, onEventDrop],
  );

  const handleSelectSlot = createHandleSelectSlot(onSelectSlot);

  // Combined: salon hours. Split: each staff column's own hours (+ ad-hoc
  // overrides), like the day view.
  const slotPropGetter = useMemo(
    () =>
      resources
        ? createSlotPropGetter(resources, overridesMap)
        : createSalonSlotPropGetter(businessInfo),
    [resources, overridesMap, businessInfo],
  );

  // Custom formats for week view
  const weekFormats = {
    dayFormat: (date: Date, culture: string | undefined, localizer: any) => {
      const dayName = localizer.format(date, "ddd", culture);
      const dayNumber = localizer.format(date, "D", culture);
      return `${dayName} ${dayNumber}`;
    },
    eventTimeRangeStartFormat: () => "",
    eventTimeRangeEndFormat: () => "",
  };

  return (
    <div
      className={`${styles.calendarContainer} ${styles.weekViewContainer} ${split ? styles.weekSplit : ""}`}
      data-week-display={weekDisplay}
      ref={containerRef}
      style={zoomStyle}
    >
      <EventBlockContext.Provider value={blockContext}>
        <DnDCalendar
          localizer={localizer}
          events={events as any}
          startAccessor="start"
          endAccessor="end"
          tooltipAccessor={tooltipAccessor as any}
          defaultView="week"
          date={selectedDate}
          step={GRID_STEP_MINS}
          timeslots={GRID_TIMESLOTS}
          selectable
          enableAutoScroll={false}
          getNow={() => getSalonNow(timezone)}
          onSelectEvent={handleSelectEvent as any}
          onDoubleClickEvent={handleDoubleClickEvent as any}
          onSelectSlot={handleSelectSlot}
          onNavigate={setSelectedDate}
          resources={resources}
          resourceIdAccessor="id"
          resourceTitleAccessor="title"
          formats={weekFormats}
          components={WEEK_COMPONENTS}
          views={["week"]}
          dayLayoutAlgorithm={(split ? SPLIT_LAYOUT : COMBINED_LAYOUT) as any}
          eventPropGetter={eventPropGetter as any}
          slotPropGetter={slotPropGetter}
          showMultiDayTimes
          onEventDrop={handleEventDrop as any}
          onEventResize={onEventResize as any}
          draggableAccessor={dragAccessor as any}
          resizableAccessor={resizeAccessor as any}
          resizable={resizable}
        />
      </EventBlockContext.Provider>
    </div>
  );
};

export default WeekView;
